import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { GeneralSetting } from '../../types/admin';
import { Save } from 'lucide-react';

interface GeneralSettingsFormProps {
  settings: GeneralSetting[];
  onUpdate: (id: string, value: string) => Promise<unknown>;
}

export const GeneralSettingsForm: React.FC<GeneralSettingsFormProps> = ({ settings, onUpdate }) => {
  const [editing, setEditing] = useState<Record<string, string>>({});

  const handleChange = (id: string, value: string) => {
    setEditing(prev => ({ ...prev, [id]: value }));
  };

  const handleSave = async (id: string) => {
    if (editing[id] !== undefined) {
      await onUpdate(id, editing[id]);
      setEditing(prev => { const next = { ...prev }; delete next[id]; return next; });
    }
  };

  return (
    <div className="space-y-3">
      {settings.map(setting => {
        const currentValue = editing[setting.setting_id] !== undefined ? editing[setting.setting_id] : setting.setting_value;
        return (
          <div key={setting.setting_id} className="flex items-center gap-4 p-4 bg-slate-900/60 border border-slate-800 rounded-xl">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-200">{setting.description}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">{setting.setting_key}</div>
            </div>
            <div className="w-48">
              {setting.config_type === 'boolean' ? (
                <select
                  value={currentValue}
                  onChange={e => handleChange(setting.setting_id, e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-xs text-slate-100"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              ) : setting.config_type === 'number' ? (
                <Input
                  type="number"
                  value={currentValue}
                  onChange={e => handleChange(setting.setting_id, e.target.value)}
                />
              ) : (
                <Input
                  value={currentValue}
                  onChange={e => handleChange(setting.setting_id, e.target.value)}
                />
              )}
            </div>
            {editing[setting.setting_id] !== undefined && editing[setting.setting_id] !== setting.setting_value && (
              <Button variant="primary" size="sm" onClick={() => handleSave(setting.setting_id)} icon={<Save className="w-3 h-3" />}>
                Save
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};
